/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-signatory.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';

import { ITemplate } from '@components/reports/i-template';
import { ItemWithPrefixedRecordId } from '@interfaces/report-templates.interface';
import { useBoolean, useTranslation } from 'hooks';

import { SAVE_REPORT_SIGNATORY, GET_REPORT_SIGNATORIES } from 'queries/reports';

import Constants from 'utils/constants';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import { ReportSignatoryFields } from '@queries/generated/fragments.generated';

const initialState = {
    role: 'AUTHOR',
    firstName: '',
    lastName: '',
    title: '',
};

interface Props {
    open: boolean;
    studyId: string;
    signatory: (ReportSignatoryFields & ItemWithPrefixedRecordId) | null;
    template: ITemplate;
    canEdit: boolean;
    onClose: (action: string) => void;
}

const EditSignatory = ({ onClose, open, signatory, studyId, template, canEdit }: Props) => {
    const { t } = useTranslation();

    const [saveSignatory] = useMutation(SAVE_REPORT_SIGNATORY);

    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({} as any);

    const disabledButton = useBoolean(true);
    const saving = useBoolean(false);

    const roles = [
        { id: 'AUTHOR', text: t('reports:author') },
        { id: 'REVIEWER', text: t('reports:reviewer') },
        { id: 'APPROVER', text: t('reports:approver') },
    ];

    useEffect(() => {
        if (open)
            setState({
                title: signatory?.title ?? state.title,
                firstName: signatory?.firstName ?? state.firstName,
                lastName: signatory?.lastName ?? state.lastName,
                role: signatory?.role ?? state.role,
            });
    }, [open, signatory]);

    const close = (action?: string | any) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({});
            disabledButton.setTrue();
        }
        onClose(action);
    };

    const submit = () => {
        const errorRequired = { role: state.role === Constants.ObjectId.Empty ? t('ra:requiredFieldError') : null };
        const errorTitle = validateFields(state.title, 'title', 150, true);
        const errorFirstName = validateFields(state.firstName, 'firstName', 70, true);
        const errorLastName = validateFields(state.lastName, 'lastName', 70, true);

        if (errorRequired.role || errorTitle.title || errorFirstName.firstName || errorLastName.lastName) {
            setErrors({
                ...errors,
                ...errorRequired,
                ...errorTitle,
                ...errorFirstName,
                ...errorLastName,
            });
            return;
        }

        saving.setTrue();

        saveSignatory({
            variables: {
                studyId,
                template,
                signatory: {
                    id: signatory?.id ?? null,
                    ...state,
                },
            },
            refetchQueries: [
                {
                    query: GET_REPORT_SIGNATORIES,
                    variables: {
                        studyId,
                        template,
                    },
                },
            ],
        })
            .then(() => {
                close(signatory ? 'edit' : 'add');
                saving.setFalse();
            })
            .catch(() => {
                saving.setFalse();
                close('error');
            });
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disabledButton.setFalse();
        let length = 70;
        if (name === 'title') length = 150;

        setState({
            ...state,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, false);
    };

    return (
        <CustomDialog onClose={close} fullWidth maxWidth="md" scroll="paper" open={open} className="edit-cv-dialog">
            <DialogTitle id="edit-signatory-modal-title">
                {signatory
                    ? t('reports:editSignatoryModal', { id: signatory._itemId || signatory.recordId || signatory.id })
                    : t('reports:addSignatory')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 25 }}>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <SelectWithCategories
                            showWarning={false}
                            customClassName="role"
                            fieldSelect="role"
                            onChange={onChange}
                            label={`${t('role')}*`}
                            filterable={false}
                            labelField="role"
                            dataArray={roles}
                            customId="select-outlined-type"
                            currentState={state.role}
                            error={errors.role}
                        />
                    </FormControl>
                    <TextField
                        label={`${t('reports:jobTitle')}*`}
                        id="edit-signatory-title"
                        fullWidth
                        value={state.title}
                        onChange={onChange('title')}
                        placeholder={t('admin:enterJobTitle')}
                        helperText={
                            <CharactersRemaining
                                id="edit-signatory-dialog-title"
                                length={state.title.length}
                                max={150}
                                error={errors.title}
                            />
                        }
                        error={!!errors.title}
                        variant="outlined"
                        color="secondary"
                    />
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={`${t('firstName')}*`}
                            id="edit-signatory-first-name"
                            fullWidth
                            value={state.firstName}
                            onChange={onChange('firstName')}
                            placeholder={t('admin:enterFirstName')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-signatory-dialog-first-name"
                                    length={state.firstName.length}
                                    max={70}
                                    error={errors.firstName}
                                />
                            }
                            error={!!errors.firstName}
                            variant="outlined"
                            color="secondary"
                        />
                    </FormControl>
                    <TextField
                        label={`${t('lastName')}*`}
                        id="edit-signatory-last-name"
                        fullWidth
                        value={state.lastName}
                        onChange={onChange('lastName')}
                        placeholder={t('admin:enterLastName')}
                        helperText={
                            <CharactersRemaining
                                id="edit-signatory-dialog-last-name"
                                length={state.lastName.length}
                                max={70}
                                error={errors.lastName}
                            />
                        }
                        error={!!errors.lastName}
                        variant="outlined"
                        color="secondary"
                    />
                </div>
            </DialogContent>
            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${!signatory && 'flex-end'}`}
            >
                <ModalUpdatedInfo item={signatory} />
                <Button
                    className="add-signatory-btn"
                    id="edit-signatory-btn"
                    disabled={
                        disabledButton.value ||
                        Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                        saving.value ||
                        !canEdit
                    }
                    text={signatory ? 'update' : 'add'}
                    onClick={submit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditSignatory;
